import { isFrenchSingular, pluralWord } from '@gozoki/tools'
import axios from 'axios'

// `cn` vit désormais dans @gozoki/ui (source unique, partagée avec store-app).
// Les formateurs métier ci-dessous restent locaux au back-office.
export { cn } from '@gozoki/ui/lib/utils'

/**
 * Suffixe pluriel français (`''` ou `'s'`) selon `count`, règle de `isFrenchSingular`.
 * En français, 0 et 1 sont singuliers (« 0 permission manquante »).
 * Ex. : `Permission${frenchPluralSuffix(n)} manquante${frenchPluralSuffix(n)}`.
 */
export function frenchPluralSuffix(count: number): '' | 's' {
  return isFrenchSingular(count) ? '' : 's'
}

export function formatPercentage(
  percentage: number,
  options?: { withSign?: boolean; withPercent?: boolean },
) {
  const { withSign = true, withPercent = true } = options ?? {}

  let result = ''
  if (Number.isNaN(percentage) || percentage === Number.NEGATIVE_INFINITY) {
    result = '0'
  } else if (percentage === Number.POSITIVE_INFINITY) {
    result = '100'
  } else {
    result = percentage < 10 ? percentage.toFixed(1) : formatNumber(percentage)
  }

  result = `${result.replace('.', ',')}${withPercent ? '%' : ''}`

  if (withSign) {
    result = `${percentage > 0 ? '+' : ''}${result}`
  }

  return result
}

export function getAxiosErrorMessage(error: unknown, fallbackMessage?: string) {
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'message' in error.response.data &&
    typeof error.response.data.message === 'string'
  ) {
    return error.response.data.message
  }

  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'error' in error.response.data &&
    typeof error.response.data.error === 'string'
  ) {
    return error.response.data.error
  }

  // 422 VineJS : premier message du validateur.
  if (
    axios.isAxiosError(error) &&
    error.response?.data &&
    'errors' in error.response.data &&
    Array.isArray(error.response.data.errors) &&
    typeof error.response.data.errors[0]?.message === 'string'
  ) {
    return error.response.data.errors[0].message as string
  }

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

export function getTuyauErrorMessage(error: unknown, fallbackMessage?: string) {
  if (error && typeof error === 'object' && 'response' in error) {
    const body = (error as { response?: unknown }).response
    if (body && typeof body === 'object') {
      if ('error' in body && typeof body.error === 'string') return body.error
      if ('message' in body && typeof body.message === 'string') return body.message
      if (
        'errors' in body &&
        Array.isArray(body.errors) &&
        typeof body.errors[0]?.message === 'string'
      ) {
        return body.errors[0].message
      }
    }
  }

  if (error instanceof Error && error.message) return error.message

  return fallbackMessage ?? 'Une erreur inconnue est survenue, veuillez contacter le support'
}

/** Message d'un refus sans explication métier (`throwForbidden`, `RbacForbiddenException`). */
export const GENERIC_FORBIDDEN_MESSAGE = 'Forbidden'

/**
 * Message d'erreur d'un appel API, client axios (legacy) ou Tuyau : celui du backend, sinon
 * `fallbackMessage`. Deux messages du backend n'expliquent rien à l'utilisateur et cèdent la place
 * au fallback (français) de l'écran : le « Forbidden » générique et celui d'une erreur serveur
 * (« Something went wrong » en production).
 */
export function getApiErrorMessage(error: unknown, fallbackMessage: string) {
  const status = getTuyauErrorStatus(error)
  if (status !== null && status >= 500) return fallbackMessage
  const message = axios.isAxiosError(error)
    ? getAxiosErrorMessage(error, fallbackMessage)
    : getTuyauErrorMessage(error, fallbackMessage)
  return message === GENERIC_FORBIDDEN_MESSAGE ? fallbackMessage : message
}

/**
 * Statut HTTP de la réponse en erreur : client Tuyau (`TuyauHTTPError.status`) ou axios
 * (`error.response.status`, legacy). `null` quand la requête n'a pas eu de réponse (réseau coupé,
 * délai dépassé : `TuyauNetworkError`, axios sans `response`) ou pour toute autre erreur.
 */
export function getTuyauErrorStatus(error: unknown): number | null {
  if (axios.isAxiosError(error)) return error.response?.status ?? null
  if (!error || typeof error !== 'object' || !('status' in error)) return null
  const { status } = error as { status?: unknown }
  return typeof status === 'number' ? status : null
}

/** Délai (secondes, > 0) d'un 429 qui porte `{ retryAfter }` dans son corps ; `null` sinon. */
export function getTuyauRetryAfterSeconds(error: unknown): number | null {
  if (getTuyauErrorStatus(error) !== 429) return null
  const body =
    error && typeof error === 'object' && 'response' in error
      ? (error as { response?: { retryAfter?: unknown } }).response
      : undefined
  const retryAfter = body?.retryAfter
  return typeof retryAfter === 'number' && retryAfter > 0 ? retryAfter : null
}

export interface ApiFieldError<Field extends string> {
  field: Field
  message: string
}

/** `errors` d'un corps 422 : `error.response` pour Tuyau, `error.response.data` pour axios (legacy). */
const validationErrorsOf = (error: unknown): unknown[] | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) return null
  const { response } = error as { response?: { errors?: unknown; data?: { errors?: unknown } } }
  if (!response || typeof response !== 'object') return null
  if (Array.isArray(response.errors)) return response.errors
  if (Array.isArray(response.data?.errors)) return response.data.errors
  return null
}

/** Entrées bien formées d'une réponse 422, avec leur règle VineJS (`null` sans elle). */
const validationEntriesOf = (
  error: unknown,
): (ApiFieldError<string> & { rule: string | null })[] => {
  const result: (ApiFieldError<string> & { rule: string | null })[] = []
  for (const entry of validationErrorsOf(error) ?? []) {
    if (!entry || typeof entry !== 'object') continue
    const { field, message, rule } = entry as { field?: unknown; message?: unknown; rule?: unknown }
    if (typeof field === 'string' && typeof message === 'string') {
      result.push({ field, message, rule: typeof rule === 'string' ? rule : null })
    }
  }
  return result
}

/**
 * Entrées `{ field, message }` d'une réponse 422 VineJS (`{ errors: [...] }`), d'une erreur Tuyau
 * comme d'une erreur axios (`@gozoki/api`, legacy), chemins complets conservés (`demand.12.palis`,
 * `hubIds.0`) ; les entrées mal formées sont ignorées. Vide hors réponse de validation.
 */
export function getApiValidationErrors(error: unknown): ApiFieldError<string>[] {
  return validationEntriesOf(error).map(({ field, message }) => ({ field, message }))
}

/**
 * Règle VineJS (`rule`) du premier refus 422 sur `field`, parmi les mêmes entrées que
 * `getApiValidationErrors`, quand deux refus d'un même champ appellent des messages différents.
 * `null` sans refus ni règle.
 */
export function getApiValidationRule(error: unknown, field: string): string | null {
  return validationEntriesOf(error).find((entry) => entry.field === field)?.rule ?? null
}

/**
 * Erreurs de champ d'une réponse 422 (cf. `getApiValidationErrors`) rapportées aux champs d'un
 * formulaire : `fields` est soit la table champ de l'API → champ du formulaire, soit la liste des
 * champs du formulaire qui portent le même nom côté API. Un champ inconnu est ignoré (il part en
 * toast chez l'appelant) ; un chemin se lit par son plus long préfixe connu : `channels.0` (élément
 * d'un tableau) se lit `channels`, `driver.carrierId` (champ d'un bloc) se lit `driver.carrierId`
 * s'il est connu, `driver` sinon.
 */
export function getApiFieldErrors<Field extends string>(
  error: unknown,
  fields: Readonly<Record<string, Field>> | readonly Field[],
): ApiFieldError<Field>[] {
  const result: ApiFieldError<Field>[] = []
  for (const { field: apiField, message } of getApiValidationErrors(error)) {
    const field = formFieldOfApiField(apiField, fields)
    if (field !== undefined) result.push({ field, message })
  }
  return result
}

/** Champ du formulaire d'un champ d'erreur de l'API (même règle que `getApiFieldErrors`). */
export function formFieldOfApiField<Field extends string>(
  apiField: string,
  fields: Readonly<Record<string, Field>> | readonly Field[],
): Field | undefined {
  const fieldOf = (path: string): Field | undefined => {
    if (Array.isArray(fields)) {
      return (fields as readonly string[]).includes(path) ? (path as Field) : undefined
    }
    const table = fields as Readonly<Record<string, Field>>
    return Object.hasOwn(table, path) ? table[path] : undefined
  }
  const segments = apiField.split('.')
  for (let length = segments.length; length > 0; length -= 1) {
    const field = fieldOf(segments.slice(0, length).join('.'))
    if (field !== undefined) return field
  }
  return undefined
}

export function trimSeconds<T extends string | null>(time: T): T {
  if (!time) return time
  return time.slice(0, 5) as T
}

export const numberFormatter = new Intl.NumberFormat('fr-FR')
export const euroFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

export const formatNumber = (value: number) => numberFormatter.format(Math.round(value))

export const formatCentsToEuro = (value: number) => euroFormatter.format(Math.round(value / 100))

export const formatNumberKEuros = (value: number) =>
  `${numberFormatter.format(Math.round(value / 1000))}k €`

// Forme d'un mot selon `count`, même règle que `frenchPluralSuffix` : partagée avec le backend.
export { pluralWord }

/** `count` suivi du mot accordé (cf. `pluralWord`) : « 1 hub », « 3 hubs », « 0 boutique ». */
export const pluralize = (count: number, singular: string, plural?: string): string =>
  `${count} ${pluralWord(count, singular, plural)}`

const frenchListFormat = new Intl.ListFormat('fr-FR', { type: 'conjunction' })

/** Une énumération en français : « Marc », « Marc et Julie », « Marc, Julie et Tom ». */
export const frenchList = (items: readonly string[]): string => frenchListFormat.format(items)
